import { useGlobalContext } from '@honeycomb/data';
import { useMemo } from 'react';
const defaultCurrencyOptions = {
    style: 'currency',
    maximumFractionDigits: 0,
};
/**
 * Formats a numeric amount as a localized currency string using the current global context.
 *
 * This hook leverages `Intl.NumberFormat` to format a given number as a currency string
 * based on the user's current `cultureCode` and `currencyCode` from the global context.
 *
 * You can optionally override the default formatting via `Intl.NumberFormatOptions`.
 *
 * @param {FormatCurrencyProps} params
 * @param {number} params.amount - The numeric value to format as currency.
 * @param {Intl.NumberFormatOptions} [params.options] - Optional override options for formatting.
 * @returns {string} A formatted currency string (e.g. "£1,000", "AED 500", "$1,200.50").
 */
export const useFormatCurrency = ({ amount, options }) => {
    const { currentContext } = useGlobalContext();
    const { cultureCode, currencyCode, displayCurrencyCode } = currentContext;
    const formatted = useMemo(() => {
        if (Number.isNaN(amount)) {
            return '';
        }
        const formatOptions = Object.assign(Object.assign(Object.assign({}, defaultCurrencyOptions), { currency: displayCurrencyCode !== null && displayCurrencyCode !== void 0 ? displayCurrencyCode : currencyCode }), options);
        return new Intl.NumberFormat(cultureCode, formatOptions).format(amount);
    }, [amount, displayCurrencyCode, currencyCode, options, cultureCode]);
    return formatted;
};
